import { DeviceProtocol } from '@hid/device/device_model'
import { BaseDriver } from './device'
import type { FnFactoryResetProvider } from './fn_factory_reset'
import { DeviceChange, DeviceChangeType, ResDeviceState } from '@libs/types/response'
import { DeviceConnectMode, MagnetSeniorType, SeniorKMEType, SpecificItem } from '@libs/types/enum'
import { nd68Magnet } from './magent/nd68'
import { joinBytes, splitIntoBytes } from './tool'
import { KIO, KeyCode, KeyFunction, Layout, OutputMode, Record, KK, SetMagnetSenior, VirtualMars68, VirtualND63Max, VK } from '@hid/define/define'
import { HIDFromVk, HidFromKK, VKFromHID, allKeysForDevice, vkFromKK } from '@hid/define/define_fn'
import { deviceManager } from '@hid/device/device_manager'
import { isRoyuanKmeMode, RoyuanMagnetMode, RoyuanMagnetOptions, RoyuanRTStabMap, RoyuanReportMap, RoyuanSendMessage, RoyuanSetKeyType, nd68Data } from './data_conversion/nd68'
import { decodeRoyuanFnKey, getRoyuanDefaultFnRawKeyMap, getRoyuanFnActionKeyByKeyMap, getRoyuanSupportedDeviceFunctionActionKeys, hasRoyuanDefaultFnLayout } from './data_conversion/registry/royuan'
import { defaultVK, keys as keyDefinitions } from '@hid/define/define_keys'
import { delay, generateMessageID } from '@libs/utils/tools'
import { deviceMode } from '@hid/define/define_list'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import firmware from '@/upgrade/firmware'
import defineType from '@hid/define/define_type'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'

const royuanBaseKeySignatureToVk = new Map<string, VK>()
const royuanMouseDvkToVk = new Map<number, VK>()

const createRoyuanBaseKeySignature = (keyMap: number[]): string => keyMap.map((value) => value & 0xff).join(',')

const getRoyuanBaseKeySignatureToVk = (): ReadonlyMap<string, VK> => {
  if (royuanBaseKeySignatureToVk.size > 0) {
    return royuanBaseKeySignatureToVk
  }
  keyDefinitions.forEach((key) => {
    try {
      const encoded = nd68Data.encodeKey({
        Input: {
          KK: 0 as KK,
          WithFN: false,
          WithModifier: [],
        },
        Output: {
          Mode: OutputMode.Key,
          VK: key.VirtualKey,
          DataIndex: -1,
          Extension: [],
        },
      })
      royuanBaseKeySignatureToVk.set(createRoyuanBaseKeySignature(encoded), key.VirtualKey)
    } catch (error) {
      void error
    }
  })

  return royuanBaseKeySignatureToVk
}

const getRoyuanMouseDvkToVk = (): ReadonlyMap<number, VK> => {
  if (royuanMouseDvkToVk.size > 0) {
    return royuanMouseDvkToVk
  }

  keyDefinitions.forEach((key) => {
    const dvk = key.DVKs.get(VirtualMars68)
    if (dvk !== undefined) {
      royuanMouseDvkToVk.set(dvk, key.VirtualKey)
    }
  })

  return royuanMouseDvkToVk
}

// 容圆方案
export class nd68Driver extends BaseDriver {
  updateFirmware(): Promise<boolean> {
    throw new Error('Method not implemented.')
  }
  declare magnet: nd68Magnet | undefined
  private royuanMessageManageIns: RoyuanMessageManage | undefined = undefined
  connectMode: DeviceConnectMode = DeviceConnectMode.M_USB

  isRemoveReportID = false
  removeReportIDKey = 'isRemoveReportID'

  power = 0

  constructor(public DeviceHID: DeviceProtocol) {
    super(DeviceHID)
    this.magnet = new nd68Magnet(this.DeviceHID, this)
    this.initConnectMode()
    this.initIsRemoveReportID()
  }
  async pair24g() {
    return 0
  }
  async syncDecorationLed() {}

  private initIsRemoveReportID() {
    const isRemove = localStorage.getItem(this.removeReportIDKey)
    if (isRemove != null && isRemove == 'true') {
      this.isRemoveReportID = true
    }
  }

  private initConnectMode() {
    const mode = deviceMode[deviceManager.deviceID]
    if (mode > DeviceConnectMode.Normal) {
      this.connectMode = mode
    }
  }

  async getVersion() {
    const sendData = Array(64).fill(0)
    sendData[0] = 0x8f
    const res = await this.sendData(sendData)
    // const id = res[1] << 24 | res[2] << 16 | res[3] << 8 | res[4]
    // console.log("id", id)
    const version = (res[8] << 8) | res[7]
    return version.toString(16)
  }

  async syncCustomColor() {
    const colorsCustom = this.save.ColorsCustom
    const lightMode = this.save.LightMode
    if (this.changeProfileFlag && colorsCustom.size > 0) {
      await delay(200)
    }
    const keysColor: number[] = Array(126 * 3).fill(0)

    for (const [key, color] of colorsCustom) {
      const baseIndex = key * 3
      keysColor[baseIndex] = (color >> 16) & 0xff
      keysColor[baseIndex + 1] = (color >> 8) & 0xff
      keysColor[baseIndex + 2] = color & 0xff
    }

    // The Royuan custom-light command carries at most 56 colour bytes per
    // report.  Keep the final page's real length: declaring it as a full page
    // makes some firmware versions consume the padding bytes as LED data.
    const pageLen = 56
    const keysGroup: number[][] = []
    let keysData: number[] = []
    for (let index = 0; index < keysColor.length; index++) {
      keysData.push(keysColor[index])
      if (index == keysColor.length - 1 || keysData.length == pageLen) {
        keysGroup.push(keysData)
        keysData = []
      }
    }

    const letData = await this.getLed()
    if (letData[0] != 0x0d) {
      // 如果未开启自定义灯光，则开启
      await this.setLed(0x0d, 0, lightMode.Light, 0, false, [0, 0, 0])
    }

    for (let index = 0; index < keysGroup.length; index++) {
      const keys = keysGroup[index]
      const isWrite = index == keysGroup.length - 1 ? true : false
      await this.setCustomLed(0xff, index, keys, isWrite)

      // A response from wired devices can arrive before the LED controller has
      // consumed the packet.  Do not let consecutive image-colour pages race
      // the controller; wireless writes already have their own queue but are
      // safe with the same small gap.
      if (!isWrite) {
        await delay(12)
      }
    }
  }

  private async setCustomLed(index: number, page: number, data: number[], isWrite: boolean = false) {
    const setData = Array(64).fill(0)
    setData[0] = 0x0c
    setData[1] = 0x00 // pic
    setData[2] = index // 设置全局/某个按键
    setData[3] = page // page //共6page
    setData[4] = data.length // len
    setData[5] = isWrite ? 0x01 : 0x00 // 是否存入固件

    // 数据部分3byte为一组，分别表示RGB
    data.forEach((color, index) => {
      setData[8 + index] = color
    })
    let lastError: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await this.sendData(setData)
        return
      } catch (error) {
        lastError = error
        if (attempt < 2) {
          await delay(20 * (attempt + 1))
        }
      }
    }
    throw lastError
  }

  async syncLeds() {
    const lightMode = this.save.LightMode
    const color = [0, 0, 0]
    if (!lightMode.FullColor) {
      color[0] = (lightMode.Color >> 16) & 0xff
      color[1] = (lightMode.Color >> 8) & 0xff
      color[2] = lightMode.Color & 0xff
    }
    await this.setLed(lightMode.Mode, lightMode.Speed, lightMode.Light, lightMode.Direction, lightMode.FullColor, color)
  }

  // 灯光direction  0:right 1:left 2:down 3:up
  private checkLedDirection(direction: number) {
    if (direction == 3) {
      direction = 2
    } else if (direction == 2) {
      direction = 3
    }
    return direction
  }

  private async setLed(mode: number, speed: number, light: number, direction: number, isFullColor: boolean, color: number[] = []) {
    let colorType = isFullColor ? 8 : 7 // 实际键盘、文档
    if (deviceManager.deviceID == VirtualND63Max) {
      colorType = isFullColor ? 7 : 8 // TODO: 魔极客 ND63
    }
    let colorAndDirection = (this.checkLedDirection(direction) << 4) | colorType

    const setData = Array(64).fill(0)
    setData[0] = 0x07
    setData[1] = mode //模式
    setData[2] = speed //速度
    setData[3] = light //亮度
    setData[4] = colorAndDirection //播放模式
    setData[5] = color[0] // R
    setData[6] = color[1] // G
    setData[7] = color[2] // B
    setData[8] = (0xff - (setData[0] + setData[1] + setData[2] + setData[3] + setData[4] + setData[5] + setData[6] + setData[7])) & 0xff
    await this.DeviceHID.send(setData, true)
  }

  private async getLed() {
    const setData = Array(64).fill(0)
    setData[0] = 0x87
    return await this.sendData(setData)
  }

  async syncKeys(): Promise<void> {
    const keys = this.save.Keys
    if (this.changeProfileFlag) {
      const needSetCount = keys.filter((key) => key.Output.Mode == OutputMode.Key)
      if (needSetCount.length > 0) {
        await delay(200)
      } else {
        return
      }
    }
    const keysMode = await this.getKeysMode()

    const setKeyMode: Map<KK, RoyuanMagnetMode> = new Map()
    for (const [key, mode] of keysMode) {
      setKeyMode.set(key, mode)
    }

    const setKeysData: Map<KK, number[]>[] = []
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      if ((key.Input.KK as any) === -1) continue
      if (key.Output.Mode == OutputMode.Key) {
        let mode = keysMode.get(key.Input.KK)
        if (mode == undefined) {
          mode = RoyuanMagnetMode.NORMAL
        }

        if (!key.Input.WithFN && (mode & 0x0f) != RoyuanMagnetMode.NORMAL) {
          setKeyMode.set(key.Input.KK, mode & 0xf0)
        }

        if (key.Output.VK == defaultVK) {
          if (key.Input.WithFN) {
            await this.setFnKey(key.Input.KK, [])
          } else {
            this.makeKeyIndexData(setKeysData, key.Input.KK, [])
          }
          continue
        }

        const setData = nd68Data.encodeKey(key)
        if (key.Input.WithFN) {
          await this.setFnKey(key.Input.KK, setData)
        } else {
          this.makeKeyIndexData(setKeysData, key.Input.KK, setData)
        }
      }
    }
    await this.setKeyIndexMap(setKeysData, true)
    await delay(200)
    await this.setKeysMode(setKeyMode, true)
    await delay(300)
    this.clearResetKeys()
    if (!this.changeProfileFlag) {
      await this.magnet?.clearKME()
    }
  }

  private makeKeyIndexData(setKeysData: Map<KK, number[]>[], key: KK, setData: number[]) {
    if ((key as any) === -1) return
    const isReset = setData.length > 0 ? false : true
    const hid = HidFromKK(key, deviceManager.deviceID)

    for (let index = 0; index < 3; index++) {
      if (setKeysData[index] == undefined) {
        setKeysData[index] = new Map()
      }
      if (!isReset && index == 0) {
        setKeysData[index].set(key, setData)
        continue
      }
      setKeysData[index].set(key, [0, 0, hid, 0])
    }
  }

  async syncMacros(): Promise<void> {
    if (this.changeProfileFlag) {
      const needSetCount = this.save.Keys.filter((key) => key.Output.Mode == OutputMode.Macro)
      if (needSetCount.length > 0) {
        await delay(200)
      } else {
        return
      }
    }

    const keysMode = await this.getKeysMode()
    const setKeyMode: Map<KK, RoyuanMagnetMode> = new Map()
    for (const [key, mode] of keysMode) {
      setKeyMode.set(key, mode)
    }

    const keys = this.save.Keys
    const macros = this.save.Macros

    const setKeysData: Map<KK, number[]>[] = []
    for (let index = 0; index < keys.length; index++) {
      const item = keys[index]
      if ((item.Input.KK as any) === -1) continue
      if (item.Output.Mode == OutputMode.Macro) {
        const macro = macros.get(item.Output.DataIndex)
        if (macro && macro.length > 0) {
          const setKeyData = nd68Data.encodeKey(item)

          let mode = keysMode.get(item.Input.KK)
          if (mode == undefined) {
            mode = RoyuanMagnetMode.NORMAL
          }

          if (!item.Input.WithFN && (mode & 0x0f) != RoyuanMagnetMode.NORMAL) {
            setKeyMode.set(item.Input.KK, mode & 0xf0)
          }

          if (item.Input.WithFN) {
            await this.setFnKey(item.Input.KK, setKeyData)
          } else {
            this.makeKeyIndexData(setKeysData, item.Input.KK, setKeyData)
          }
          await this.setMacro(item, macro)
        }
      }
    }

    await this.setKeyIndexMap(setKeysData, true)
    await delay(200)
    await this.setKeysMode(setKeyMode, true)
    await delay(300)

    await this.magnet?.clearKME()
  }

  private async setMacro(key: KIO, macro: Record[]) {
    const macors: number[][] = []
    let macroData: number[] = []

    macro.forEach((item, index) => {
      const hid = HIDFromVk(item.Key)
      if (!hid) {
        throw new Error('set macro hid not found')
      }
      macroData.push(hid)
      const delayBase = item.Press ? 0x80 : 0x00
      if (item.Delay > 127) {
        const [lowByte, highByte] = splitIntoBytes(item.Delay)
        macroData.push(...[delayBase, lowByte, highByte])
      } else {
        const delay = delayBase == 0x00 ? 1 : delayBase
        macroData.push(delay + item.Delay)
      }
      if (macroData.length == 0x36 || index == macro.length - 1) {
        macors.push(macroData)
        macroData = []
      }
    })
    for (let index = 0; index < macors.length; index++) {
      const macro = macors[index]

      const setData = Array(64).fill(0)
      setData[0] = 0x0b
      setData[1] = key.Output.DataIndex
      setData[2] = index // page
      setData[3] = 0x38 // len
      setData[4] = index == macors.length - 1 ? 0x01 : 0x00 // isEnd

      setData[8] = key.Output.Extension[0] == 1 ? key.Output.Extension[1] : 1 // times
      setData[9] = 0x00

      let baseIndex = 10
      if (index > 0) {
        baseIndex = 8
      }
      macro.forEach((item, index) => {
        setData[baseIndex + index] = item
      })

      setData.push(...macro)
      await this.sendData(setData)
    }
    await delay(200)
  }

  async syncConfig(): Promise<void> {
    await this.setAllSpecificItem()
  }

  async getDeviceState(): Promise<ResDeviceState> {
    const res: ResDeviceState = {
      ConnectType: this.connectMode,
      IsConnect: true,
      IsSleep: false,
    }
    return res
  }

  async reset(): Promise<void> {
    const data = Array(64).fill(0)
    data[0] = 0x01
    await this.sendData(data)
    if (this.changeProfileFlag) {
      await delay(1000)
    } else {
      await delay(2000)
    }
  }

  private async setAllSpecificItem() {
    const items = this.save.Items
    for (const [key, item] of items) {
      if (key == SpecificItem.PowerIsCharging || key == SpecificItem.PowerLevel) {
        continue
      }
      await this.setOrReadSpecificItem(key, true, item)
    }
  }

  async setOrReadSpecificItem(item: string, isWrite: boolean, value: any): Promise<any> {
    const item2 = item as SpecificItem
    if (item2 == SpecificItem.RTStab) {
      return await this.setOrReadRTStab(isWrite, value)
    }

    if (item2 == SpecificItem.PreventAccidentalTouch) {
      return await this.setOrReadPreventAccidentalTouch(isWrite, value)
    }

    if (item2 == SpecificItem.BluetoothSleepTime) {
      return await this.setOrReadSleepTime(isWrite, false, value)
    }

    if (item2 == SpecificItem.SleepTime) {
      return await this.setOrReadSleepTime(isWrite, true, value)
    }

    if (item2 == SpecificItem.PollingRate) {
      const res = await this.setOrReadRate(isWrite, value)
      return res
    }

    if (item2 == SpecificItem.PowerLevel && !isWrite) {
      return this.getDevicePower()
    }
    if (item == SpecificItem.PowerIsCharging) {
      const version = await firmware.versionRoYuanV304()
      if (version) {
        return await this.getPowerIsCharging()
      }
    }

    return null
  }

  private async setOrReadPreventAccidentalTouch(isWrite: boolean, value: number) {
    const data = await this.readKeyboardInfo()
    if (!isWrite) {
      return data[3]
    }
    data[0] = 0x09
    data[3] = value ? 0x01 : 0x00
    await this.sendData(data)
    await delay(100)
    return
  }

  private async setOrReadRTStab(isWrite: boolean, value: number) {
    const data = await this.readKeyboardInfo()
    if (!isWrite) {
      return RoyuanRTStabMap.get(data[4])
    }
    let setIndex = 0
    RoyuanRTStabMap.forEach((item, index) => {
      if (item == value) {
        setIndex = index
      }
    })
    data[0] = 0x09
    data[4] = setIndex
    await this.sendData(data)
    await delay(100)
    return
  }

  private KeyboardInfo: number[] = []
  private async readKeyboardInfo() {
    if (this.KeyboardInfo.length == 64) {
      return this.KeyboardInfo
    }
    const data = Array(64).fill(0)
    data[0] = 0x89
    const res = await this.sendData(data)
    if (this.KeyboardInfo.length == 0) {
      this.KeyboardInfo = res
      setTimeout(() => {
        this.KeyboardInfo = []
      }, 2000)
    }
    return res
  }

  private async setOrReadRate(isWrite: boolean, value: number) {
    let setIndex = 0
    if (isWrite) {
      RoyuanReportMap.forEach((item, index) => {
        if (item == value) {
          setIndex = index
        }
      })
    }
    const sendData = Array(64).fill(0)
    sendData[0] = isWrite ? 0x03 : 0x83
    sendData[2] = isWrite ? setIndex : 0x00
    const res = await this.sendData(sendData)
    if (!isWrite) {
      return RoyuanReportMap.get(res[2])
    }
    const version = await firmware.versionRoYuanV304()
    if (version) {
      await delay(300)
    } else {
      await delay(100)
    }
  }

  private async setOrReadSleepTime(isWrite: boolean, is24g: Boolean, value: number) {
    const data = await this.readSleepTime()
    if (!isWrite) {
      if (is24g) {
        return joinBytes([data[11], data[10]])
      }
      return joinBytes([data[9], data[8]])
    }
    data[0] = 0x11
    const [lowByte, highByte] = splitIntoBytes(value)
    if (is24g) {
      data[10] = lowByte
      data[11] = highByte
    } else {
      data[8] = lowByte
      data[9] = highByte
      data[12] = 1 //背光休眠后，就进入深睡模式
      data[13] = 0
    }
    await this.sendData(data)
    await delay(100)
    return
  }

  private sleepTimeData: number[] = []
  private async readSleepTime() {
    if (this.sleepTimeData.length == 64) {
      return this.sleepTimeData
    }
    const sendData = Array(64).fill(0)
    sendData[0] = 0x91
    const res = await this.sendData(sendData)
    if (this.sleepTimeData.length == 0) {
      this.sleepTimeData = res
      setTimeout(() => {
        this.sleepTimeData = []
      }, 2000)
    }
    return res
  }

  private async getDevicePower() {
    return this.power
  }

  private async getPowerIsCharging() {
    const send = Array(64).fill(0)
    send[0] = 0x82
    const res = await this.sendData(send)
    //0，设备不充电状态；1，设备正在充电；2，设备满电
    return res[2]
  }

  // private sendKeyboardData(isWrite: boolean, enableFn: boolean = false, enableAntiShake: boolean = false, rtStab: number = 0) {
  //     const data = Array(64).fill(0);
  //     data[0] = isWrite ? 0x09 : 0x89;
  //     data[2] = enableFn ? 0x01 : 0x00;
  //     data[3] = enableAntiShake ? 0x01 : 0x00;
  //     data[4] = rtStab;
  //     return data
  // }

  // async setKeys(key: KK, setData: number[], magnetIndex: number = 0) {
  //     return await this.setKey(key, setData, magnetIndex, true)
  // }

  private async setFnKey(key: number = 0, setData: number[] = [], isWrite: boolean = true) {
    const data = Array(64).fill(0)
    data[0] = isWrite ? 0x10 : 0x90
    data[3] = key // 设置的按键位号
    data[5] = 0x38 // 长度

    setData.forEach((item, index) => {
      data[8 + index] = item
    })
    const res = await this.sendData(data)
    await delay(100)
    return res
  }

  private async getFnLayerPageResponses(): Promise<Map<number, number[]>> {
    const keys = allKeysForDevice(deviceManager.deviceID)
    const pageResponses = new Map<number, number[]>()

    if (keys.length === 0) {
      return pageResponses
    }

    const maxKey = Math.max(...keys)
    const recordsPerPage = 16
    const pageCount = Math.ceil((maxKey + 1) / recordsPerPage)

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const data = Array(64).fill(0)
      data[0] = 0x90
      data[1] = 0x00
      data[2] = 0x00
      data[3] = 0xff
      data[4] = pageIndex
      const res = await this.sendData(data)
      pageResponses.set(pageIndex, res)
    }

    return pageResponses
  }

  private getFnLayerRawKeyMapFromPageResponses(pageResponses: Map<number, number[]>): Map<KK, number[]> {
    const keyMap = new Map<KK, number[]>()
    const keys = allKeysForDevice(deviceManager.deviceID)
    const keySet = new Set(keys)

    if (keys.length === 0) {
      return keyMap
    }

    const maxKey = Math.max(...keys)
    const recordsPerPage = 16

    for (let pageIndex = 0; pageIndex < Math.ceil((maxKey + 1) / recordsPerPage); pageIndex++) {
      const res = pageResponses.get(pageIndex)
      if (res === undefined) {
        continue
      }

      for (let recordIndex = 0; recordIndex < recordsPerPage; recordIndex++) {
        const key = pageIndex * recordsPerPage + recordIndex
        if (key > maxKey) {
          break
        }

        if (!keySet.has(key as KK)) {
          continue
        }

        const payloadStart = recordIndex * 4
        keyMap.set(key as KK, res.slice(payloadStart, payloadStart + 4))
      }
    }

    return keyMap
  }

  private async getFnLayerRawKeyMap(): Promise<Map<KK, number[]>> {
    const pageResponses = await this.getFnLayerPageResponses()
    return this.getFnLayerRawKeyMapFromPageResponses(pageResponses)
  }

  async getFnKeyMapOverlay(): Promise<Map<KK, number>> {
    const keyMap = new Map<KK, number>()
    const rawKeyMap = await this.getFnLayerRawKeyMap()

    rawKeyMap.forEach((rawKeyData, kk) => {
      const vk = decodeRoyuanFnKey(rawKeyData, deviceManager.deviceID)
      if (vk !== undefined) {
        keyMap.set(kk, vk)
      }
    })

    console.log('getFnKeyMapOverlay', keyMap)

    return keyMap
  }

  async getKeyLayoutOverlay(): Promise<Partial<Layout> | undefined> {
    const deviceKeys = allKeysForDevice(deviceManager.deviceID)
    const rawKeyMap = await this.getNormalLayerRawKeyMap()
    const keysMode = await this.getKeysMode(deviceKeys)
    const keyMap = new Map<KK, number>()
    const keyMapModifier = new Map<KK, number[]>()
    const magnetMap = await this.getMagnetLayoutOverlay(keysMode)

    rawKeyMap.forEach((rawKeyData, kk) => {
      const keyMode = keysMode.get(kk)
      if (keyMode !== undefined && (keyMode & 0x0f) !== RoyuanMagnetMode.NORMAL) {
        return
      }

      const decoded = this.decodeNormalLayerKey(rawKeyData, kk)
      if (decoded === undefined) {
        return
      }

      if (decoded.type === 'key') {
        keyMap.set(kk, decoded.vk)
        return
      }

      if (decoded.type === 'modifier') {
        keyMapModifier.set(kk, decoded.keys)
        return
      }
    })

    return {
      KeyMap: keyMap,
      KeyMapModifier: keyMapModifier,
      Magenet: magnetMap,
    }
  }

  private async getNormalLayerRawKeyMap(magnetIndex: number = 0): Promise<Map<KK, number[]>> {
    const keyMap = new Map<KK, number[]>()
    const keys = allKeysForDevice(deviceManager.deviceID)
    const keySet = new Set(keys)

    if (keys.length === 0) {
      return keyMap
    }

    const maxKey = Math.max(...keys)
    const firstPayload = await this.getKeyMatrixPagePayload(0, magnetIndex)
    const recordsPerPage = Math.max(1, Math.floor(firstPayload.length / 4))
    const pageCount = Math.ceil((maxKey + 1) / recordsPerPage)

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const payload = pageIndex === 0 ? firstPayload : await this.getKeyMatrixPagePayload(pageIndex, magnetIndex)
      for (let recordIndex = 0; recordIndex < recordsPerPage; recordIndex++) {
        const key = pageIndex * recordsPerPage + recordIndex
        if (key > maxKey) {
          break
        }
        if (!keySet.has(key as KK)) {
          continue
        }

        const payloadStart = recordIndex * 4
        keyMap.set(key as KK, payload.slice(payloadStart, payloadStart + 4))
      }
    }

    return keyMap
  }

  private async getKeyMatrixPagePayload(pageIndex: number, magnetIndex: number = 0): Promise<number[]> {
    const request = Array(64).fill(0)
    request[0] = 0x8a
    request[1] = 0x00
    request[2] = 0xff
    request[3] = pageIndex
    request[4] = magnetIndex
    const response = await this.sendData(request)

    if (response[0] === request[0] && response[1] === request[1] && response[2] === request[2] && response[3] === request[3]) {
      return response.slice(8)
    }

    return response
  }

  private async getMagnetLayoutOverlay(keysMode: Map<KK, number>): Promise<Map<KK, SetMagnetSenior>> {
    const magnetMap = new Map<KK, SetMagnetSenior>()
    const magnetKeys = Array.from(keysMode.entries()).filter(([, mode]) => {
      const actionMode = mode & 0x0f
      return actionMode === RoyuanMagnetMode.DKS || actionMode === RoyuanMagnetMode.MT || actionMode === RoyuanMagnetMode.TGL || actionMode === RoyuanMagnetMode.TGL_Continue || isRoyuanKmeMode(actionMode)
    })

    if (magnetKeys.length === 0) {
      return magnetMap
    }

    const startTravel = await this.getMagnet(RoyuanMagnetOptions.DYNAMIC_PRESS_TRAVEL)

    for (const [kk, mode] of magnetKeys) {
      const actionMode = mode & 0x0f

      if (actionMode === RoyuanMagnetMode.DKS) {
        const travelData = (startTravel[kk * 2] | (startTravel[kk * 2 + 1] << 8)) / 10
        const keyFunctionData = await this.getKeyMagnet(RoyuanMagnetOptions.DYNAMIC_KEY_FLAG, kk)
        let bottomTravel = 32 // 容圆的触底行程是固定死写的，不可更改，固件里也没有存储这个值
        if (specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) {
          bottomTravel = 32 * 2
        }
        const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeDKS)
        const layerRawKeys = await Promise.all([0, 1, 2, 3].map((magnetIndex) => this.getNormalLayerRawKey(kk, magnetIndex)))
        for (let layerIndex = 0; layerIndex < magnetData.Keys.length; layerIndex++) {
          magnetData.Keys[layerIndex].KeyCode = this.decodeRoyuanMagnetKeyCode(layerRawKeys[layerIndex])
          magnetData.Keys[layerIndex].KeyFunction = this.decodeRoyuanMagnetKeyFunction(keyFunctionData[layerIndex] ?? 0)
          magnetData.Keys[layerIndex].Travel = layerIndex == 1 || layerIndex == 2 ? bottomTravel : travelData
        }
        magnetMap.set(kk, magnetData)
        continue
      }

      if (actionMode === RoyuanMagnetMode.MT) {
        const mtDurationData = await this.getKeyMagnet(RoyuanMagnetOptions.MT_LONG_PRESS_TIME, kk)
        const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeMT)
        const layerRawKeys = await Promise.all([0, 1].map((magnetIndex) => this.getNormalLayerRawKey(kk, magnetIndex)))
        magnetData.MTDuration = mtDurationData[0] ?? 0
        magnetData.Keys[0].KeyCode = this.decodeRoyuanMagnetKeyCode(layerRawKeys[0])
        magnetData.Keys[1].KeyCode = this.decodeRoyuanMagnetKeyCode(layerRawKeys[1])
        magnetMap.set(kk, magnetData)
        continue
      }

      if (actionMode === RoyuanMagnetMode.TGL || actionMode === RoyuanMagnetMode.TGL_Continue) {
        const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeTGL)
        magnetData.TGLMode = actionMode === RoyuanMagnetMode.TGL_Continue ? RoyuanMagnetMode.TGL_Continue : RoyuanMagnetMode.TGL
        magnetData.Keys[0].KeyCode = this.decodeRoyuanMagnetKeyCode(await this.getNormalLayerRawKey(kk, 0))
        magnetMap.set(kk, magnetData)
        continue
      }

      if (isRoyuanKmeMode(actionMode)) {
        const kmeData = await this.getKeyMagnet(RoyuanMagnetOptions.KME, kk)
        const bindKey = kmeData[0]
        const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeKME)
        magnetData.KMEMode = this.decodeRoyuanKMEMode(actionMode)
        if (bindKey !== undefined && bindKey !== 0xff) {
          try {
            const bindVk = vkFromKK(bindKey as KK, deviceManager.deviceID)
            if (bindVk !== undefined) {
              magnetData.Keys[0].KeyCode = [bindVk, 0]
            }
          } catch (error) {
            void error
          }
        }
        magnetMap.set(kk, magnetData)
      }
    }

    return magnetMap
  }

  private async getNormalLayerRawKey(kk: KK, magnetIndex: number = 0): Promise<number[]> {
    const request = Array(64).fill(0)
    request[0] = 0x8a
    request[1] = 0x00
    request[2] = kk
    request[3] = 0x00
    request[4] = magnetIndex
    const response = await this.sendData(request)

    if (response[0] === request[0] && response[1] === request[1] && response[2] === request[2] && response[3] === request[3]) {
      return response.slice(8, 12)
    }

    return response.slice(0, 4)
  }

  private createDefaultMagnetSeniorData(type: MagnetSeniorType): SetMagnetSenior {
    return {
      KeyType: type,
      Keys: [
        { KeyCode: [0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
      ],
      MTDuration: 0,
      TGLEnable: 0,
      TGLMode: RoyuanMagnetMode.TGL,
      KMEMode: this.decodeRoyuanKMEMode(0),
      DelayTime: 0,
    }
  }

  private decodeRoyuanMagnetKeyCode(rawKeyData?: number[]): KeyCode {
    if (rawKeyData === undefined) {
      return [0, 0]
    }

    const normalized = rawKeyData.map((value) => value & 0xff)
    if (normalized.every((value) => value === 0)) {
      return [0, 0]
    }

    if (normalized[0] === RoyuanSetKeyType.Mouse) {
      const mouseDvk = normalized[2] | (normalized[3] << 8)
      const mouseVk = getRoyuanMouseDvkToVk().get(mouseDvk)
      return mouseVk !== undefined ? [mouseVk, 0] : [0, 0]
    }

    const keyCodes = normalized
      .slice(1)
      .filter((value) => value !== 0)
      .map((hid) => VKFromHID(hid))
      .filter((value): value is VK => value !== undefined)
    return [keyCodes[0] ?? 0, keyCodes[1] ?? 0, keyCodes[2] ?? 0]
  }

  private decodeRoyuanMagnetKeyFunction(flagValue: number): KeyFunction {
    // 与写侧 hid/driver/magent/nd68.ts 中 setTrPSx 对称：每区域占 2 bit，低区域在低位。
    //   pair 00 → 0（不激活）
    //   pair 01 → 1（单次按下）
    //   pair 10 / 11 → 激活（2 或 3）:
    //     前一对为 10（在本区域起始边界处释放了）→ 3（释放后再按下）
    //     首个激活区域，或前一对为 11（穿越边界保持按压）→ 2（持续按压）
    const keyFunction: [number, number, number, number] = [0, 0, 0, 0]
    let prevPair = -1

    for (let i = 0; i < 4; i++) {
      const pair = (flagValue >> (i * 2)) & 0x03
      if (pair === 0b00) {
        keyFunction[i] = 0
      } else if (pair === 0b01) {
        keyFunction[i] = 1
      } else {
        // pair is 0b10 or 0b11 — active zone
        keyFunction[i] = prevPair === 0b10 ? 3 : 2
      }
      prevPair = pair
    }

    return keyFunction
  }

  private decodeRoyuanKMEMode(mode: number): [number, number] {
    switch (mode) {
      case RoyuanMagnetMode.KME_AFirst:
      case SeniorKMEType.AFirst:
        return [0, 1]
      case RoyuanMagnetMode.KME_BFirst:
      case SeniorKMEType.BFirst:
        return [1, 0]
      case RoyuanMagnetMode.KME_Neutral:
      case SeniorKMEType.Neutral:
        return [1, 1]
      default:
        return [0, 0]
    }
  }

  private decodeNormalLayerKey(rawKeyData: number[], kk: KK): { type: 'key'; vk: VK } | { type: 'modifier'; keys: VK[] } | { type: 'macro'; macroIndex: number; macroRawType: number } | undefined {
    const normalized = rawKeyData.map((value) => value & 0xff)
    if (normalized.every((value) => value === 0)) {
      return undefined
    }

    const actionKey = getRoyuanFnActionKeyByKeyMap(normalized as [number, number, number, number], deviceManager.deviceID)
    if (actionKey !== undefined) {
      return { type: 'key', vk: actionKey as VK }
    }

    if (normalized[0] === RoyuanSetKeyType.Macro) {
      return {
        type: 'macro',
        macroIndex: normalized[2],
        macroRawType: normalized[1],
      }
    }

    const modifierKeys = this.decodeModifierKey(normalized)
    if (modifierKeys !== undefined) {
      return { type: 'modifier', keys: modifierKeys }
    }

    const vk = this.decodeBaseKey(normalized)
    if (vk === undefined) {
      return undefined
    }

    try {
      if (vkFromKK(kk, deviceManager.deviceID) === vk) {
        return undefined
      }
    } catch (error) {
      void error
    }

    return { type: 'key', vk }
  }

  private decodeBaseKey(normalized: number[]): VK | undefined {
    const mappedVk = getRoyuanBaseKeySignatureToVk().get(createRoyuanBaseKeySignature(normalized))
    if (mappedVk !== undefined) {
      return mappedVk
    }

    if (normalized[0] !== RoyuanSetKeyType.Keyboard) {
      return undefined
    }

    const hidCandidates = normalized.slice(1).filter((value) => value !== 0)
    if (hidCandidates.length !== 1) {
      return undefined
    }

    return VKFromHID(hidCandidates[0])
  }

  private decodeModifierKey(normalized: number[]): VK[] | undefined {
    if (normalized[0] !== RoyuanSetKeyType.Keyboard) {
      return undefined
    }

    const hidCandidates = normalized.slice(1).filter((value) => value !== 0)
    if (hidCandidates.length < 2 || hidCandidates.length > 3) {
      return undefined
    }

    const keys = hidCandidates.map((hid) => VKFromHID(hid))
    if (keys.some((value) => value === undefined)) {
      return undefined
    }

    const resolvedKeys = keys as VK[]
    const modifiers = resolvedKeys.slice(0, -1)
    if (modifiers.some((value) => !defineType.isModifier(value))) {
      return undefined
    }

    return resolvedKeys
  }

  private async getMacroRawData(macroIndex: number): Promise<number[]> {
    const payload: number[] = []

    for (let pageIndex = 0; pageIndex < 4; pageIndex++) {
      const request = Array(64).fill(0)
      request[0] = 0x8b
      request[1] = macroIndex
      request[2] = pageIndex
      const response = await this.sendData(request)
      payload.push(...response)
    }

    return payload
  }

  private decodeMacroRawData(rawData: number[]): { repeatCount: number; records: Record[] } | undefined {
    if (rawData.length < 2) {
      return undefined
    }

    const repeatCount = joinBytes([rawData[0], rawData[1]])
    const records: Record[] = []

    for (let index = 2; index < rawData.length - 1; ) {
      const code = rawData[index]
      const attribute = rawData[index + 1]

      if (code === 0x00 && attribute === 0x00) {
        break
      }

      if (code === 0xfa) {
        break
      }

      let delayValue = 0
      let step = 2
      const shortDelay = attribute & 0x7f
      if (shortDelay === 0) {
        if (index + 3 >= rawData.length) {
          return undefined
        }
        delayValue = joinBytes([rawData[index + 2], rawData[index + 3]])
        step = 4
      } else {
        delayValue = shortDelay
      }

      const vk = VKFromHID(code)
      if (vk === undefined) {
        return undefined
      }

      records.push({
        Key: vk,
        Press: (attribute & 0x80) !== 0,
        Delay: delayValue,
      })
      index += step
    }

    return {
      repeatCount,
      records,
    }
  }

  private decodeMacroLoopType(macroRawType: number, repeatCount: number): number {
    if (macroRawType === 1) {
      return 2
    }
    if (macroRawType === 2) {
      return 3
    }
    if (repeatCount > 1) {
      return 1
    }
    return 0
  }

  get fnFactoryReset(): FnFactoryResetProvider | undefined {
    if (!hasRoyuanDefaultFnLayout(deviceManager.deviceID)) return undefined
    return {
      resetToDefault: async (kk) => {
        const defaultKeyMap = getRoyuanDefaultFnRawKeyMap(kk, deviceManager.deviceID)
        if (defaultKeyMap === undefined) return false
        await this.setFnKey(kk, defaultKeyMap)
        return true
      },
    }
  }

  getSupportedDeviceFunctionKeys(): VK[] {
    return [...getRoyuanSupportedDeviceFunctionActionKeys(deviceManager.deviceID)] as VK[]
  }

  async setKeyIndexMap(keys: Map<KK, number[]>[], isFlashEnable: boolean = false) {
    for (let index = 0; index < keys.length; index++) {
      const setData = keys[index]
      if (setData != undefined && setData.size > 0) {
        const isEnd = index == keys.length - 1 ? true : false
        let setKeyIndex = 0
        for (let [kk, data] of setData) {
          setKeyIndex++
          const isWrite = isEnd && setKeyIndex == setData.size && isFlashEnable ? true : false
          await this.setKey(kk, data, index, isWrite)
        }
      }
    }
  }

  private async setKey(key: number = 0, setData: number[] = [], magnetIndex: number = 0, isFlashEnable: boolean = false) {
    const data = Array(64).fill(0)
    data[0] = 0x0a
    data[1] = 0
    data[2] = key
    data[5] = isFlashEnable ? 0x01 : 0x00 //固件保存数据
    data[6] = magnetIndex // 对应磁轴四层按键
    setData.forEach((item, index) => {
      data[8 + index] = item
    })
    return await this.sendData(data)
  }

  // private async setGlobalKey(page: number = 0, setData: number[] = [], magnetIndex: number = 0, isFlashEnable: boolean = false) {
  //     const data = Array(64).fill(0);
  //     data[0] = 0x0a;
  //     data[1] = 0;
  //     data[2] = 0xFF;  // 全局设置
  //     data[3] = page;   // 设置的页数
  //     data[4] = 0x38;  // 长度
  //     data[5] = isFlashEnable ? 0x01: 0x00;  //固件保存数据
  //     data[6] = magnetIndex;  // 对应磁轴四层按键
  //     setData.forEach((item, index) => {
  //         data[8 + index] = item
  //     })
  //     return await this.sendData(data)
  // }

  // private async setKeys(keys: Map<KK, number[]>[], isFlashEnable: boolean = false) {
  //     let indexCount = 0
  //     const indexData:number[][] = []
  //     for (let index = 0; index < keys.length; index++) {
  //         const keysData = keys[index];
  //         if(keysData.size > 0){
  //             indexCount++

  //             const sendData = Array(126*4).fill(0);
  //             keysData.forEach((v, i) => {
  //                 const baseIndex = i * 4
  //                 sendData[baseIndex] = v[0] ? v[0] : 0;
  //                 sendData[baseIndex + 1] = v[1] ? v[1] : 0;
  //                 sendData[baseIndex + 2] = v[2] ? v[2] : 0;
  //                 sendData[baseIndex + 3] = v[3] ? v[3] : 0;
  //             })
  //             indexData[index] = sendData
  //         }
  //     }

  //     for (let index = 0; index < indexData.length; index++) {
  //         const keysData = indexData[index];

  //         const dataGroup:number[][] = [];
  //         let tmepData:number[] = []

  //         keysData.forEach((v, i) => {
  //             tmepData.push(v)
  //             if(tmepData.length == 0x38 || i == keysData.length - 1){
  //                 dataGroup.push(tmepData)
  //                 tmepData = []
  //             }
  //         })

  //         for (let i = 0; i < dataGroup.length; i++) {
  //             const data = dataGroup[i];
  //             const isWrite = i == dataGroup.length - 1 && index == indexCount-1 && isFlashEnable ? true : false;
  //             await this.setGlobalKey(i, data, index, isWrite)
  //         }
  //     }

  // }

  async getKeysMode(keys: KK[] = []): Promise<Map<KK, number>> {
    let res = new Map<KK, number>()
    if (keys.length == 0) {
      keys = allKeysForDevice(deviceManager.deviceID)
    }
    const keysMode: number[] = await this.getMagnet(RoyuanMagnetOptions.KEY_MODE)
    for (const key of keys) {
      res.set(key, keysMode[key])
    }
    return res
  }

  async setKeysMode(keys: Map<KK, RoyuanMagnetMode>, isFlashEnable: boolean = false) {
    const setData: number[] = Array(126).fill(0)
    for (const [key, mode] of keys) {
      setData[key] = mode
    }
    await this.setMagnet(RoyuanMagnetOptions.KEY_MODE, setData, isFlashEnable)
  }

  async getKeyMagnet(option: RoyuanMagnetOptions, key: KK) {
    const getParam = Array(64).fill(0)
    getParam[0] = 0xe5
    getParam[1] = option
    getParam[2] = 0x00 // 整体/单按键设置
    getParam[3] = key // 设置的按键位号
    const res = await this.sendData(getParam)
    return res
  }

  private optionByteNumber(option: RoyuanMagnetOptions) {
    const optionTravel = [
      RoyuanMagnetOptions.PRESS_TRAVEL,
      RoyuanMagnetOptions.RT_PRESS_TRAVEL,
      RoyuanMagnetOptions.RT_RELEASE_TRAVEL,
      RoyuanMagnetOptions.DYNAMIC_PRESS_TRAVEL,
      RoyuanMagnetOptions.DEADZONE,
      RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_VALUE,
      RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_TRAVEL,
    ]
    if (optionTravel.includes(option)) {
      return 2
    }
    return 1
  }

  async getMagnet(option: RoyuanMagnetOptions) {
    const basePage = this.magnetBasePage(option)
    const res: number[] = []
    for (let index = 0; index <= basePage; index++) {
      const getParam = Array(64).fill(0)
      getParam[0] = 0xe5
      getParam[1] = option
      getParam[2] = 0x01 // 整体/单按键设置
      getParam[3] = index // 设置的按键位号/页数
      const resSend = await this.sendData(getParam)
      //
      if (resSend[0] == getParam[0] && getParam[1] == resSend[1] && getParam[2] == resSend[2] && getParam[3] == resSend[3]) {
        throw new Error(option + 'getMagnet error:' + index)
      } else {
        if (this.isRemoveReportID == true) {
          if (option == RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_VALUE || option == RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_TRAVEL) {
            resSend[63] = 0
          } else {
            const keyRes = await this.getKeyMagnet(option, (((index + 1) * 64) / 2 - 1) as KK)
            const sliceNumber = this.optionByteNumber(option)
            const insertData = keyRes.slice(sliceNumber - 1, sliceNumber)
            for (let index = 0; index < insertData.length; index++) {
              resSend[63 - index] = insertData[index]
            }
          }
        }
        res.push(...resSend)
      }
    }
    if (this.isRemoveReportID == true && (option == RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_VALUE || option == RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_TRAVEL)) {
      for (let index = 0; index <= basePage; index++) {
        const baseIndex = ((index + 1) * 64) / 2 - 1
        if (option == RoyuanMagnetOptions.KEY_PRESS_DYNAMIC_VALUE) {
          res[baseIndex * 2] = 52
          res[baseIndex * 2 + 1] = 8
        } else {
          res[baseIndex * 2] = 0
          res[baseIndex * 2 + 1] = 0
        }
      }
    }

    return res
  }

  async setKeyMagnet(option: RoyuanMagnetOptions, key: KK, setData: number[] = [], isFlashEnable: boolean = false) {
    if (
      option == RoyuanMagnetOptions.PRESS_TRAVEL ||
      option == RoyuanMagnetOptions.RELEASE_TRAVEL ||
      option == RoyuanMagnetOptions.RT_PRESS_TRAVEL ||
      option == RoyuanMagnetOptions.RT_RELEASE_TRAVEL ||
      option == RoyuanMagnetOptions.DYNAMIC_PRESS_TRAVEL ||
      option == RoyuanMagnetOptions.DEADZONE
    ) {
      setData = splitIntoBytes(setData[0])
    }
    return await this.setMagnetSend(option, false, setData, key, isFlashEnable)
  }

  private magnetBasePage(option: RoyuanMagnetOptions) {
    let basePage = 3
    if (option == RoyuanMagnetOptions.KEY_MODE || option == RoyuanMagnetOptions.MT_LONG_PRESS_TIME || option == RoyuanMagnetOptions.KME || option == RoyuanMagnetOptions.AXIS_TYPE) {
      basePage = 1
    }
    if (option == RoyuanMagnetOptions.DYNAMIC_KEY_FLAG2) {
      basePage = 7
    }
    return basePage
  }

  async setMagnet(option: RoyuanMagnetOptions, setData: number[] = [], isWrite: boolean = false) {
    const dataGroup: number[][] = []
    let data: number[] = []

    setData.forEach((value, index) => {
      data.push(value)
      if (data.length == 56 || index == setData.length - 1) {
        dataGroup.push(data)
        data = []
      }
    })

    for (let index = 0; index < dataGroup.length; index++) {
      const data = dataGroup[index]
      //const isWrite = index == dataGroup.length - 1 ? true : false
      const aa = index == dataGroup.length - 1 && isWrite ? true : false
      const resSend = await this.setMagnetSend(option, true, data, index, aa)
      if (resSend[0] == data[0] && data[1] == resSend[1] && data[2] == resSend[2] && data[3] == resSend[3]) {
        throw new Error(option + 'setMagnet error:' + index)
      }
    }
  }

  private async setMagnetSend(option: RoyuanMagnetOptions, isGlabal: boolean, data: number[], index: number, isWrite: boolean) {
    const setValue = Array(64).fill(0)
    setValue[0] = 0x65
    setValue[1] = option
    setValue[2] = isGlabal ? 0x01 : 0x00 // 整体/单按键设置
    setValue[3] = index // 设置的页数
    setValue[4] = isWrite ? 0x01 : 0x00 // 是否写入固件

    data.forEach((value, index) => {
      setValue[index + 8] = value
    })
    const ret = await this.sendData(setValue)
    return ret
  }

  async sendData(data: number[]): Promise<number[]> {
    if (this.connectMode == DeviceConnectMode.M_USB) {
      const res = await RoyuanSendMessage(data, this.DeviceHID, this.isRemoveReportID)
      this.checkRemoveReportID(data, res)
      return res
    }
    if (this.connectMode == DeviceConnectMode.Wireless24G) {
      if (this.royuanMessageManageIns == undefined) {
        this.royuanMessageManageIns = new RoyuanMessageManage(this.DeviceHID, this)
      }
      const res = await this.royuanMessageManageIns.send(data)
      this.checkRemoveReportID(data, res)
      return res
    }
    throw new Error('connectMode is undefined')
  }

  private async checkRemoveReportID(sendData: number[], data: number[]) {
    if (sendData[0] != 0x8f) {
      return
    }
    const isRemove = localStorage.getItem(this.removeReportIDKey)
    if (isRemove != null) {
      return
    }

    if (navigator.userAgent.indexOf('cherry_ast_embed') > -1) {
      this.setRemoveReportID(false)
      return
    }

    if (data[0] == 0x00 && data[1] == 0x8f) {
      this.setRemoveReportID(true)
      return
    }
    this.setRemoveReportID(false)
  }

  private setRemoveReportID(value: boolean) {
    if (value) {
      localStorage.setItem(this.removeReportIDKey, 'true')
      location.reload()
    } else {
      localStorage.setItem(this.removeReportIDKey, 'false')
    }
  }

  async syncDynamicColor(): Promise<boolean> {
    return true
  }
}

interface RoyuanMessage {
  isWrite: boolean
  data: number[]
  resolveFn: (data: number[]) => void
}

/**
 * 消息管理
 * 无线状态时要去询问键盘是否可用，键盘是否可读写
 *
 */
class RoyuanMessageManage {
  private messageList: RoyuanMessage[] = []
  private isSleep = false

  constructor(
    private DeviceHID: DeviceProtocol,
    private parent: nd68Driver,
  ) {
    this.handleMessage()
  }

  private async handleMessage() {
    for (let index = 0; true; index++) {
      const task = this.messageList.shift()
      if (task == undefined) {
        await this.checkStatus()
        await delay(20)
        continue
      }

      const taskRes = await this.sendMessage(task.data)
      if (!task.isWrite) {
        const res = await this.reciveMessage(task.data[0])
        task.resolveFn(res)
      } else {
        task.resolveFn(taskRes)
      }
    }
  }

  private async sendMessage(data: number[]): Promise<number[]> {
    for (let index = 0; true; index++) {
      const status = await this.checkStatus()
      if (data[0] == 0x8f) {
        if ((status[3] == 0x00 && status[5] == 1) || (status[4] == 0x00 && status[6] == 1)) {
          return await this.sendData(data)
        } else {
          await delay(20)
        }
      } else {
        if (status[3] == 0x00 && status[5] == 1) {
          return await this.sendData(data)
        } else {
          await delay(20)
        }
      }
    }
  }

  private async reciveMessage(sendType: number): Promise<number[]> {
    for (let index = 0; true; index++) {
      const status = await this.checkStatus()
      if (sendType == 0x8f) {
        if ((status[3] == 0x00 && status[0] == 1) || (status[4] == 0x00 && status[1] == 1)) {
          return await this.sendRevice()
        } else {
          await delay(20)
        }
      } else {
        if (status[3] == 0x00 && status[0] == 1) {
          return await this.sendRevice()
        } else {
          await delay(20)
        }
      }
    }
  }

  private async sendRevice() {
    const reviceData = Array(64).fill(0)
    reviceData[0] = 0xfc
    reviceData[1] = 0x0a
    return await this.sendData(reviceData)
  }

  private async notifyDeviceStatus(sleep: number, power: number) {
    const isSleep = sleep == 0x01 ? true : false
    if (isSleep != this.isSleep) {
      this.isSleep = isSleep
      const data: DeviceChange = {
        ChangeType: DeviceChangeType.status,
        State: {
          ConnectType: DeviceConnectMode.Wireless24G,
          IsSleep: this.isSleep,
          IsConnect: true,
        },
      } as DeviceChange
      this.notifyStatus(data)
    }
    if (power != this.parent.power) {
      this.parent.power = power
      const data: DeviceChange = {
        ChangeType: DeviceChangeType.power,
      } as DeviceChange
      this.notifyStatus(data)
    }
  }

  private notifyStatus(data: DeviceChange) {
    fakeSocket.onmessage({
      data: JSON.stringify({
        ID: generateMessageID(),
        Type: 0xf2,
        Data: data,
        Error: null,
      }),
    } as MessageEvent)
  }

  private async checkStatus(): Promise<number[]> {
    const checkStatus = Array(64).fill(0)
    checkStatus[0] = 0xf7
    const res = await this.sendData(checkStatus)
    let sleep = res[3]
    let power = res[1]
    if (sleep != 0 && sleep != 1) {
      sleep = res[4]
      power = res[2]
    }
    this.notifyDeviceStatus(sleep, power)
    return res
  }

  async send(data: number[]): Promise<number[]> {
    return new Promise((resolve) => {
      const message: RoyuanMessage = {
        isWrite: this.isWrite(data),
        data: data,
        resolveFn: resolve,
      }
      this.messageList.push(message)
    })
  }

  private readList: number[] = [0xe5, 0x8f, 0x82, 0x83, 0x86, 0x91, 0x84, 0x85, 0x89, 0x87, 0x8a, 0x8b, 0x90]
  private isWrite(data: number[]): boolean {
    let res = true
    if (this.readList.includes(data[0])) {
      res = false
    }
    return res
  }

  private async sendData(data: number[]): Promise<number[]> {
    let res = await RoyuanSendMessage(data, this.DeviceHID, this.parent.isRemoveReportID)
    return res
  }
}
